import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { ArrowUpRight } from '@phosphor-icons/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import portraitImage from '../../assets/02.JPG';
import { IntroClock } from './IntroClock';
import { ScatterText } from './ScatterText';
import {
  LetterRevealSection,
  type LetterRevealCopy,
  type LetterRevealCoverImageMetrics,
} from './LetterRevealSection';

gsap.registerPlugin(ScrollTrigger);

const PORTRAIT_SCROLL_SHIFT = 36;

const INTRO_UPDATES: Array<{ text: string; to?: string }> = [
  {
    text: 'My board game won “Most Complete” at Kiwijam Auckland 2026',
    to: '/ensnared',
  },
  { text: 'SnapLingo — a vocabulary-learning app launching soon' },
];

const UPDATE_CONFETTI_COLORS = [
  '#ff6451',
  '#1bc9fb',
  '#a661ea',
  '#fbc145',
  '#52d15e',
  '#f7ffb3',
];
const UPDATE_CONFETTI_PARTICLE_COUNT = 56;
const UPDATE_CONFETTI_DURATION = 900;

type UpdateConfettiParticle = {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  gravity: number;
  color: string;
};

const launchUpdateConfetti = (target: HTMLElement, container: HTMLElement | null) => {
  if (
    !container ||
    container.querySelector('.intro-stack__confetti-canvas') ||
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const canvasWidth = Math.max(1, Math.round(targetRect.width));
  const canvasHeight = 160;
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement('canvas');

  canvas.className = 'intro-stack__confetti-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.width = Math.round(canvasWidth * pixelRatio);
  canvas.height = Math.round(canvasHeight * pixelRatio);
  canvas.style.width = `${canvasWidth}px`;
  canvas.style.height = `${canvasHeight}px`;
  canvas.style.left = `${targetRect.left - containerRect.left}px`;
  canvas.style.top = `${targetRect.top - containerRect.top - 100}px`;
  container.appendChild(canvas);

  const context = canvas.getContext('2d');

  if (!context) {
    canvas.remove();
    return;
  }

  context.scale(pixelRatio, pixelRatio);

  const particles: UpdateConfettiParticle[] = Array.from(
    { length: UPDATE_CONFETTI_PARTICLE_COUNT },
    () => ({
      x: canvasWidth * (0.15 + Math.random() * 0.7),
      y: canvasHeight * 0.85,
      velocityX: (Math.random() - 0.5) * 4.8,
      velocityY: -(1.1 + Math.random() * 2.8),
      gravity: 0.06 + Math.random() * 0.04,
      color: UPDATE_CONFETTI_COLORS[
        Math.floor(Math.random() * UPDATE_CONFETTI_COLORS.length)
      ],
    })
  );
  const startedAt = performance.now();

  const drawFrame = (now: number) => {
    const progress = Math.min((now - startedAt) / UPDATE_CONFETTI_DURATION, 1);

    context.clearRect(0, 0, canvasWidth, canvasHeight);

    particles.forEach((particle) => {
      particle.x += particle.velocityX;
      particle.y += particle.velocityY;
      particle.velocityY += particle.gravity;

      context.beginPath();
      context.arc(particle.x, particle.y, 2, 0, Math.PI * 2);
      context.fillStyle = particle.color;
      context.globalAlpha = Math.max(0, 1 - progress * 1.2);
      context.fill();
    });

    context.globalAlpha = 1;

    if (progress < 1 && canvas.isConnected) {
      window.requestAnimationFrame(drawFrame);
      return;
    }

    canvas.remove();
  };

  window.requestAnimationFrame(drawFrame);
};

type IntroCoverSectionProps = {
  introCopy?: LetterRevealCopy;
  onCoverImageMetricsChange?: (metrics: LetterRevealCoverImageMetrics) => void;
  desktopBottomSection?: React.ReactNode;
  desktopFinalSection?: React.ReactNode;
  desktopOverlaySection?: React.ReactNode;
  desktopFollow?: React.ReactNode;
};

export const IntroCoverSection: React.FC<IntroCoverSectionProps> = ({
  introCopy,
  onCoverImageMetricsChange,
  desktopBottomSection,
  desktopFinalSection,
  desktopOverlaySection,
  desktopFollow,
}) => {
  const portraitRef = useRef<HTMLDivElement>(null);
  const updatesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onCoverImageMetricsChange) {
      return;
    }

    onCoverImageMetricsChange({
      imageHeight: 0,
      visibleImageOffset: 0,
    });
  }, [onCoverImageMetricsChange]);

  useLayoutEffect(() => {
    const portrait = portraitRef.current;
    const introSection = portrait?.closest<HTMLElement>('.intro-stack__intro');

    if (!portrait || !introSection) {
      return;
    }

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const portraitShift = () =>
      Math.min(PORTRAIT_SCROLL_SHIFT, Math.max(18, window.innerWidth * 0.022));
    const tween = gsap.fromTo(
      portrait,
      {
        x: () => portraitShift(),
      },
      {
        x: () => portraitShift() * -0.45,
        ease: 'none',
        scrollTrigger: {
          trigger: introSection,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.65,
          invalidateOnRefresh: true,
        },
      }
    );

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
      gsap.set(portrait, { clearProps: 'transform' });
    };
  }, []);

  useEffect(() => {
    const updates = updatesRef.current;

    if (!updates) {
      return;
    }

    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      updates.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        updates.classList.add('is-visible');
        observer.disconnect();
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    observer.observe(updates);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="intro-stack">
      <section className="scatter-screen">
        <ScatterText />
      </section>

      <div className="intro-stack__intro">
        <div className="intro-stack__clock-wrap">
          <IntroClock />
        </div>
        <div className="intro-stack__identity-visuals">
          <div
            ref={portraitRef}
            className="intro-stack__portrait intro-stack__portrait--primary"
          >
            <img src={portraitImage} alt="Mia by the Auckland waterfront" />
          </div>
        </div>
        <LetterRevealSection
          variant="full"
          copy={introCopy}
          footer={(
            <div className="intro-stack__footer">
              <div
                ref={updatesRef}
                className="intro-stack__updates"
                role="list"
                aria-label="Latest updates"
              >
                <span
                  className="intro-stack__updates-label"
                  role="listitem"
                  style={{ transitionDelay: '0ms' }}
                >
                  News
                </span>
                {INTRO_UPDATES.map((update, index) =>
                  update.to ? (
                    <Link
                      className="intro-stack__update-pill intro-stack__update-pill--link"
                      role="listitem"
                      key={update.text}
                      to={update.to}
                      style={{ transitionDelay: `${(index + 1) * 150}ms` }}
                      onMouseEnter={(event) => {
                        launchUpdateConfetti(event.currentTarget, updatesRef.current);
                      }}
                    >
                      {update.text}
                    </Link>
                  ) : (
                    <span
                      className="intro-stack__update-pill"
                      role="listitem"
                      key={update.text}
                      style={{ transitionDelay: `${(index + 1) * 150}ms` }}
                      onMouseEnter={(event) => {
                        launchUpdateConfetti(event.currentTarget, updatesRef.current);
                      }}
                    >
                      {update.text}
                    </span>
                  )
                )}
              </div>
              <Link className="intro-stack__more-link" to="/about">
                <span>more about me</span>
                <ArrowUpRight
                  className="intro-stack__more-icon"
                  size={16}
                  weight="regular"
                  aria-hidden="true"
                />
              </Link>
            </div>
          )}
        />
      </div>

      {desktopBottomSection ? (
        <div className="intro-stack__continuation intro-stack__continuation--bottom">
          {desktopBottomSection}
        </div>
      ) : null}

      {desktopFinalSection ? (
        <div className="intro-stack__continuation intro-stack__continuation--final">
          {desktopFinalSection}
        </div>
      ) : null}

      {desktopFollow ? (
        <div className="intro-stack__continuation intro-stack__continuation--follow">
          {desktopFollow}
        </div>
      ) : null}

      {desktopOverlaySection ? (
        <div className="intro-stack__continuation intro-stack__continuation--overlay">
          {desktopOverlaySection}
        </div>
      ) : null}
    </div>
  );
};
